import { schedulingEntryBoutique } from "@/lib/schedulingEntry";
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, CalendarHeart, ShieldCheck, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const SHIFT_TYPES = ["opening", "mid", "closing", "training"];

function title(value) { return value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase()); }

function Toggle({ active, onClick, children, locked = false }) {
  return <button type="button" onClick={locked ? undefined : onClick} disabled={locked}
    className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${locked ? "border-emerald-200 bg-emerald-50 text-emerald-800 cursor-default" : active ? "border-stone-900 bg-stone-900 text-white" : "border-stone-200 bg-white text-stone-600 hover:border-stone-400"}`}>
    {children}
  </button>;
}

function ChoiceRow({ label, items, selected, setSelected, locked = [] }) {
  const toggle = (item) => setSelected(selected.includes(item) ? selected.filter((value) => value !== item) : [...selected, item]);
  return <div>
    <div className="text-[11px] label-eyebrow text-stone-400 mb-2">{label}</div>
    <div className="flex flex-wrap gap-2">{items.map((item) => <Toggle key={item} active={selected.includes(item)} locked={locked.includes(item)} onClick={() => toggle(item)}>{title(item)}{locked.includes(item) ? " · required" : ""}</Toggle>)}</div>
  </div>;
}

export default function SchedulingPreferences() {
  const { user } = useAuth();
  const [entryParams] = useSearchParams();
  const entryBoutique = schedulingEntryBoutique(user, entryParams.get("boutique"));
  const boutique = entryBoutique.toUpperCase();
  const isManager = user && !["front_desk", "experience_guide", "lead_experience_guide", "massage_therapist"].includes(user.role);
  const [team, setTeam] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [preference, setPreference] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => { if (user?.user_id && !employeeId) setEmployeeId(user.user_id); }, [user, employeeId]);
  useEffect(() => {
    if (!isManager) return;
    api.get("/scheduling/team", { params: { boutique, role: "EG" } }).then((r) => {
      const people = r.data?.team || [];
      setTeam(people);
      if (!employeeId && people[0]?.employee_id) setEmployeeId(people[0].employee_id);
    }).catch(() => setTeam([]));
  }, [boutique, isManager, employeeId]);
  useEffect(() => {
    if (!employeeId) return;
    setLoading(true); setMessage(""); setError("");
    api.get("/scheduling/preferences", { params: { boutique, employee_id: employeeId } })
      .then((r) => setPreference(r.data?.preference || null))
      .catch((e) => setError(e.response?.data?.detail || "Could not load preferences."))
      .finally(() => setLoading(false));
  }, [employeeId, boutique]);

  const update = (field, value) => setPreference((current) => ({ ...current, [field]: value }));
  const save = async () => {
    if (!preference || !employeeId) return;
    setSaving(true); setMessage(""); setError("");
    try {
      const result = await api.put(`/scheduling/preferences/${employeeId}`, {
        preferred_work_days: preference.preferred_work_days || [],
        recurring_days_off: preference.recurring_days_off || [],
        preferred_shift_types: preference.preferred_shift_types || [],
        preference_strength: preference.preference_strength || "normal",
        availability_adjustment_note: preference.availability_adjustment_note || null,
      }, { params: { boutique } });
      setPreference(result.data.preference);
      setMessage(isManager ? "Preference saved for scheduling." : "Preference submitted for your GM to consider.");
    } catch (e) { setError(e.response?.data?.detail || "Could not save preferences."); }
    finally { setSaving(false); }
  };

  const editable = Boolean(preference) && (isManager || employeeId === user?.user_id);
  return <div className="min-h-screen bg-[#fbfaf6] text-stone-800">
    <div className="max-w-4xl mx-auto px-5 sm:px-10 py-6 sm:py-8">
      <div className="mt-5 flex items-start justify-between gap-5 flex-wrap">
        <div>
          <h1 className="font-serif-display text-stone-900 text-4xl sm:text-5xl ">Scheduling preferences</h1>
          <p className="text-sm text-stone-500 mt-3 max-w-2xl">Tell Sonmi what works best. Preferences help shape a proposed schedule, but your GM remains responsible for business coverage and final decisions.</p>
        </div>
        <div className="rounded-2xl bg-emerald-50 border border-emerald-100 px-4 py-3 text-xs text-emerald-900 flex items-start gap-2 max-w-xs"><ShieldCheck size={16} className="mt-0.5 flex-none" /> Saturday and Sunday availability is required for all Experience Guides.</div>
      </div>

      {isManager && <div className="mt-7 bg-white subtle-border rounded-2xl p-4"><label className="text-[11px] label-eyebrow text-stone-400 block mb-2">Editing for</label><select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} className="w-full sm:w-80 px-3 py-2 rounded-xl subtle-border bg-white text-sm outline-none" data-testid="preferences-employee">{team.map((person) => <option key={person.employee_id} value={person.employee_id}>{person.employee_name}</option>)}</select></div>}

      {loading ? <div className="mt-10 text-sm text-stone-500">Loading preferences…</div> : error && !preference ? <div className="mt-10 text-sm text-rose-600">{error}</div> : preference && <div className="mt-7 space-y-5" data-testid="scheduling-preferences">
        <section className="bg-white subtle-border rounded-3xl p-6 sm:p-7 card-shadow">
          <div className="flex items-center gap-2"><CalendarHeart size={17} className="text-rose-600" /><h2 className="font-serif-display text-xl text-stone-900">Your ideal rhythm</h2></div>
          <p className="mt-1 text-xs text-stone-500">These are scheduling signals, not guaranteed commitments.</p>
          <div className="mt-6 space-y-6">
            <ChoiceRow label="Preferred days to work" items={DAYS} selected={preference.preferred_work_days || []} setSelected={(value) => update("preferred_work_days", value)} />
            <ChoiceRow label="Preferred recurring days off" items={DAYS} selected={preference.recurring_days_off || []} setSelected={(value) => update("recurring_days_off", value)} locked={["saturday", "sunday"]} />
            <ChoiceRow label="Preferred shift experiences" items={SHIFT_TYPES} selected={preference.preferred_shift_types || []} setSelected={(value) => update("preferred_shift_types", value)} />
          </div>
        </section>

        <section className="bg-white subtle-border rounded-3xl p-6 sm:p-7 card-shadow">
          <div className="flex items-center gap-2"><Sparkles size={17} className="text-amber-600" /><h2 className="font-serif-display text-xl text-stone-900">How strongly should Sonmi weigh this?</h2></div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">{[["normal", "Normal", "Use when coverage allows."], ["strong", "Strong", "Favor it when balancing the month."], ["near_fixed", "Near-fixed", "Treat as a consistent pattern unless the GM needs otherwise."]].map(([value, label, description]) => <button key={value} type="button" onClick={() => update("preference_strength", value)} className={`rounded-2xl border p-4 text-left ${preference.preference_strength === value ? "border-stone-900 bg-stone-900 text-white" : "border-stone-200 bg-white text-stone-700 hover:border-stone-400"}`}><div className="text-sm font-medium">{label}</div><div className={`text-xs mt-1 ${preference.preference_strength === value ? "text-stone-300" : "text-stone-500"}`}>{description}</div></button>)}</div>
        </section>

        <section className="bg-white subtle-border rounded-3xl p-6 sm:p-7 card-shadow">
          <h2 className="font-serif-display text-xl text-stone-900">Availability adjustment</h2>
          <p className="mt-1 text-xs text-stone-500">Use this to explain a change your GM should review. It does not remove required weekend availability.</p>
          <textarea value={preference.availability_adjustment_note || ""} onChange={(e) => update("availability_adjustment_note", e.target.value)} rows={3} placeholder="For example: I can no longer work Wednesday evenings after September 1." className="mt-4 w-full rounded-xl subtle-border px-3 py-2 text-sm outline-none" data-testid="preferences-adjustment-note" />
        </section>
        {error && <div className="text-sm text-rose-600">{error}</div>}
        {message && <div className="rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{message}</div>}
        <div className="flex justify-end"><button type="button" onClick={save} disabled={!editable || saving} data-testid="preferences-save" className="rounded-full bg-stone-900 px-5 py-2.5 text-sm text-white disabled:opacity-40 flex items-center gap-2"><Check size={15} /> {saving ? "Saving…" : isManager ? "Save preference" : "Submit preference"}</button></div>
      </div>}
    </div>
  </div>;
}
